import React, { useState, useEffect, useCallback } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
  IconButton,
  Button,
  ButtonGroup,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Popover,
} from '@mui/material';
import { Cancel, Edit, InfoOutlined } from '@mui/icons-material';
import dayjs from 'dayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import UserSelectModal from './UserSearchModal';
import MessageCreate from '../Pages/Message/MessageCreate';
import { getLCRoleHistory, RoleHistoryResponse } from '../endpoints/chaptersTs';
import { getFundChairRoleHistory } from '../endpoints/financeFunds';
import {
  LeadershipPeer,
  shouldWarnLeadershipSelfRemoval,
} from '../utils/leadershipSelfRemovalWarning';

interface UserObject {
  id: string;
  full_name: string;
}

interface SaveData {
  id: string;
  positionId?: string;
  committeeId?: string;
  action: 'newAppointment' | 'editDates' | 'removeRecord';
  userId?: string;
  /** Current holder when replacing; used for multi-fund chair lookup. */
  incumbentUserId?: string;
  startDate?: string | null;
  endDate?: string | null;
}

interface PositionDisplayProps {
  id: string;
  position: string;
  positionId?: string;
  committeeId?: string;
  canEdit: boolean;
  name?: string;
  startDate?: string | null;
  endDate?: string | null;
  isSaving: boolean;
  errorSavingText?: string;
  onSave?: (data: SaveData) => void;
  userId?: string;
  pageFrom?: string;
  localChapterId?: string | null;
  spdChapterId?: string | null;
  fundId?: string | null;
  canMessage?: boolean;
  showRoleHistory?: boolean;
  leadershipPeers?: LeadershipPeer[];
  /** When `pageFrom` is `finance-fund-chairs`, role dropdown options (group_roles) for edit mode. */
  chairRoleOptions?: { id: string; name: string }[];
}

export default function PositionDisplay({
  id,
  position,
  positionId,
  committeeId,
  canEdit,
  name,
  startDate,
  endDate,
  isSaving,
  errorSavingText,
  onSave,
  userId,
  pageFrom,
  localChapterId = null,
  spdChapterId = null,
  fundId = null,
  canMessage = true,
  showRoleHistory = false,
  leadershipPeers = [],
  chairRoleOptions,
}: PositionDisplayProps) {
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [editDates, setEditDates] = useState<boolean>(false);
  const [newAppointment, setNewAppointment] = useState<boolean>(false);
  const [removeRecord, setRemoveRecord] = useState<boolean>(false);

  const [startDateValue, setStartDateValue] = useState<string | null>(
    startDate
  );
  const [endDateValue, setEndDateValue] = useState<string | null>(endDate);
  // For TLC: when in editDates mode, allow changing Primary vs Contact
  const [positionIdValue, setPositionIdValue] = useState<string | undefined>(
    positionId
  );

  const [userIdValue, setUserIdValue] = useState<string | null>(null);
  const [userNameValue, setUserNameValue] = useState<string | null>(name);
  const [showUserSelect, setShowUserSelect] = useState<boolean>(false);

  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [sendingSave, setSendingSave] = useState<boolean>(false);
  const [pendingSave, setPendingSave] = useState<SaveData | null>(null);
  const [showSelfRemovalWarning, setShowSelfRemovalWarning] =
    useState<boolean>(false);

  const [modalOpen, setModalOpen] = useState<boolean>(false);

  const [roleHistoryAnchor, setRoleHistoryAnchor] =
    useState<HTMLElement | null>(null);
  const [roleHistoryData, setRoleHistoryData] =
    useState<RoleHistoryResponse | null>(null);
  const [roleHistoryLoading, setRoleHistoryLoading] = useState<boolean>(false);
  const [roleHistoryError, setRoleHistoryError] = useState<string | null>(null);
  const selfRemovalWarningEnabled =
    pageFrom === 'lc-leadership' ||
    pageFrom === 'spd-leadership' ||
    leadershipPeers.length > 0;

  const handleRoleHistoryClick = async (
    event: React.MouseEvent<HTMLElement>
  ): Promise<void> => {
    if (!positionId || (!localChapterId && !fundId)) return;
    setRoleHistoryAnchor(event.currentTarget);
    setRoleHistoryData(null);
    setRoleHistoryError(null);
    setRoleHistoryLoading(true);
    try {
      const data = fundId
        ? await getFundChairRoleHistory(fundId, positionId)
        : await getLCRoleHistory(localChapterId!, positionId);
      setRoleHistoryData(data);
    } catch (err: unknown) {
      setRoleHistoryError(
        err instanceof Error ? err.message : 'Failed to load role history'
      );
    } finally {
      setRoleHistoryLoading(false);
    }
  };

  const handleRoleHistoryClose = (): void => {
    setRoleHistoryAnchor(null);
  };

  const resetState = useCallback((): void => {
    setIsEditing(false);
    setEditDates(false);
    setNewAppointment(false);
    setRemoveRecord(false);
    setStartDateValue(null);
    setEndDateValue(null);
    setPositionIdValue(positionId);
    setUserIdValue(null);
    setUserNameValue(null);
    setShowUserSelect(false);
    setErrorMessage(null);
    setPendingSave(null);
    setShowSelfRemovalWarning(false);
  }, [positionId]);

  useEffect(() => {
    if (!isSaving && sendingSave && !errorSavingText) {
      setSendingSave(false);
      resetState();
    }
    if (errorSavingText) {
      setSendingSave(false);
      setErrorMessage(errorSavingText);
    }
  }, [sendingSave, errorSavingText, isSaving, resetState]);

  const handleEditClick = (): void => {
    if (isEditing) {
      resetState();
    } else {
      setErrorMessage(null);
      setIsEditing(true);
    }
  };

  const showButtons = (): boolean => {
    if (isEditing && !editDates && !newAppointment && !removeRecord) {
      return true;
    } else {
      return false;
    }
  };

  const handleSetNewAppointment = (): void => {
    setErrorMessage(null);
    setNewAppointment(true);
    setEditDates(false);
    setRemoveRecord(false);
    // Do not carry over the current row’s start (often a past year) — new terms default to today.
    setStartDateValue(dayjs().format('YYYY-MM-DD'));
    setEndDateValue(null);
    setShowUserSelect(true);
  };

  const handleSetEditDates = (): void => {
    setErrorMessage(null);
    setEditDates(true);
    setNewAppointment(false);
    setRemoveRecord(false);
    setUserNameValue(name);
    setStartDateValue(startDate);
    setEndDateValue(endDate);
    if (pageFrom === 'tlc') {
      setPositionIdValue(positionId ?? 'Contact');
    } else {
      setPositionIdValue(positionId ?? '');
    }
  };

  const handleSetRemoveRecord = (): void => {
    setErrorMessage(null);
    setRemoveRecord(true);
    setEditDates(false);
    setNewAppointment(false);
  };

  const handleUserSelect = (userObject: UserObject): void => {
    let userId = userObject.id;
    let userName = userObject.full_name;
    setUserIdValue(userId);
    setUserNameValue(userName);
    setShowUserSelect(false);
  };

  const handleConfirmRemove = (): void => {
    const payload: SaveData = {
      id: id,
      committeeId: committeeId,
      action: 'removeRecord',
    };
    const shouldWarn =
      selfRemovalWarningEnabled &&
      shouldWarnLeadershipSelfRemoval({
        currentUserId: localStorage.getItem('user_id'),
        incumbentUserId: userId,
        rowId: id,
        peers: leadershipPeers,
        action: payload.action,
      });
    if (shouldWarn) {
      setPendingSave(payload);
      setShowSelfRemovalWarning(true);
      return;
    }
    setSendingSave(true);
    setRemoveRecord(false);
    onSave?.(payload);
  };

  const handleSave = (): void => {
    // Validate the input values
    if (newAppointment) {
      if (!userIdValue) {
        setErrorMessage('Please select a user');
        return;
      }
      if (!startDateValue) {
        setErrorMessage('Please select a start date');
        return;
      }
    }
    if (editDates) {
      if (!startDateValue) {
        setErrorMessage('Please select a start date');
        return;
      }
    }
    // Call onSave function with the new values and action (Date or New Appointment)
    let payload: SaveData | null = null;
    if (newAppointment) {
      payload = {
        id: id,
        positionId: positionId,
        committeeId: committeeId,
        action: 'newAppointment',
        userId: userIdValue,
        incumbentUserId: userId || undefined,
        startDate: startDateValue,
        endDate: endDateValue,
      };
    } else if (editDates) {
      payload = {
        id: id,
        committeeId: committeeId,
        action: 'editDates',
        startDate: startDateValue,
        endDate: endDateValue,
      };
      if (pageFrom === 'tlc' && positionIdValue !== undefined) {
        payload.positionId = positionIdValue;
      }
      if (
        pageFrom === 'finance-fund-chairs' &&
        positionIdValue !== undefined &&
        positionIdValue !== ''
      ) {
        payload.positionId = positionIdValue;
      }
    }
    if (!payload) return;
    const shouldWarn =
      selfRemovalWarningEnabled &&
      shouldWarnLeadershipSelfRemoval({
        currentUserId: localStorage.getItem('user_id'),
        incumbentUserId: userId,
        rowId: id,
        peers: leadershipPeers,
        action: payload.action,
        nextUserId: payload.userId,
        nextEndDate: payload.endDate,
      });
    if (shouldWarn) {
      setPendingSave(payload);
      setShowSelfRemovalWarning(true);
      return;
    }
    setSendingSave(true);
    onSave?.(payload);
  };

  const handleContinueAfterSelfRemovalWarning = (): void => {
    if (!pendingSave) {
      setShowSelfRemovalWarning(false);
      return;
    }
    setShowSelfRemovalWarning(false);
    setSendingSave(true);
    if (pendingSave.action === 'removeRecord') {
      setRemoveRecord(false);
    }
    onSave?.(pendingSave);
    setPendingSave(null);
  };

  const handleCancelSelfRemovalWarning = (): void => {
    setShowSelfRemovalWarning(false);
    setPendingSave(null);
    setSendingSave(false);
  };

  const sendMessage = (): void => {
    setModalOpen(true);
  };

  return (
    <Grid container item xs={12} key={id}>
      <Grid
        item
        xs={12}
        flex={1}
        flexDirection={'row'}
        justifyContent={'space-between'}
        display={'flex'}
      >
        <Typography variant="body1" fontWeight={'bold'}>
          {position}
          {showRoleHistory && positionId && (localChapterId || fundId) && (
            <IconButton
              onClick={handleRoleHistoryClick}
              sx={{ padding: 0, marginLeft: 0.5 }}
              size="small"
              aria-label="View role history"
            >
              <InfoOutlined
                fontSize="small"
                sx={{ fontSize: 18 }}
                color="action"
              />
            </IconButton>
          )}
          {canEdit && !isEditing && (
            <IconButton
              onClick={() => {
                handleEditClick();
              }}
              sx={{ padding: 0, marginLeft: 1 }}
            >
              <Edit fontSize="small" color="primary" sx={{ fontSize: 18 }} />
            </IconButton>
          )}
          {isEditing && (
            <IconButton
              onClick={() => {
                handleEditClick();
              }}
              sx={{ padding: 0, marginLeft: 1 }}
            >
              <Cancel fontSize="small" color="primary" sx={{ fontSize: 25 }} />
            </IconButton>
          )}
        </Typography>
        {!isEditing && userId && canMessage && (
          <Button
            onClick={() => {
              sendMessage();
            }}
            variant="contained"
            size={'small'}
            sx={{ fontWeight: 'bold', pb: 0, pt: 0, pl: 1, pr: 1 }}
          >
            {userId === localStorage.getItem('user_id')
              ? 'Message Myself'
              : `Message`}
          </Button>
        )}
      </Grid>
      <Grid item xs={12}>
        {name ? (
          <Typography variant="body1">{name}</Typography>
        ) : (
          <Typography variant="body1">Vacant</Typography>
        )}
      </Grid>
      <Grid container item xs={12}>
        {startDate ? (
          <Grid item xs={12}>
            <Typography variant="caption" fontSize={12} paddingBottom={1}>
              {dayjs(startDate).format('MM/DD/YYYY')} -
            </Typography>
            <Typography
              variant="caption"
              fontSize={12}
              paddingBottom={1}
              paddingLeft={1}
            >
              {endDate ? dayjs(endDate).format('MM/DD/YYYY') : 'Present'}
            </Typography>
          </Grid>
        ) : (
          <Grid item xs={12}>
            <Typography variant="caption" fontSize={12}>
              N/A
            </Typography>
          </Grid>
        )}
      </Grid>
      <Dialog
        open={isEditing}
        onClose={resetState}
        maxWidth="sm"
        fullWidth
        aria-labelledby="position-edit-dialog-title"
      >
        <DialogTitle id="position-edit-dialog-title">
          Edit: {position}
        </DialogTitle>
        <DialogContent>
          {(errorSavingText || errorMessage) && (
            <Alert
              severity="error"
              sx={{
                mb: 2,
                '& .MuiAlert-message': { color: 'rgba(0,0,0,0.87)' },
              }}
            >
              {errorSavingText || errorMessage}
            </Alert>
          )}
          {showButtons() && (
            <ButtonGroup
              variant="contained"
              aria-label="text button group"
              size="small"
              fullWidth
              sx={{ mb: 2 }}
            >
              <Button
                onClick={handleSetNewAppointment}
                color="success"
                sx={{ fontWeight: 'bold', padding: 0, pt: 1, pb: 1 }}
                disabled={isSaving || sendingSave}
              >
                Appoint New
              </Button>
              {name && (
                <Button
                  onClick={handleSetEditDates}
                  color="primary"
                  sx={{ fontWeight: 'bold', padding: 0, pt: 1, pb: 1 }}
                  disabled={isSaving || sendingSave}
                >
                  Update Dates
                </Button>
              )}
              {name && (
                <Button
                  onClick={handleSetRemoveRecord}
                  sx={{
                    fontWeight: 'bold',
                    padding: 0,
                    pt: 1,
                    pb: 1,
                    background: '#0072CE',
                  }}
                  disabled={isSaving || sendingSave}
                >
                  Delete Record
                </Button>
              )}
            </ButtonGroup>
          )}
          {(newAppointment || editDates) && !removeRecord && (
            <Grid container>
              <Divider sx={{ marginBottom: 1, width: '100%' }} />
              {newAppointment && (
                <Grid item xs={12} sx={{ mb: 1 }}>
                  <Typography variant="body1">
                    {userNameValue ? userNameValue : 'Please select a user'}
                    <IconButton
                      onClick={() => setShowUserSelect(true)}
                      sx={{ padding: 0, marginLeft: 1 }}
                      disabled={isSaving || sendingSave}
                    >
                      <Edit
                        fontSize="small"
                        color="primary"
                        sx={{ fontSize: 18 }}
                      />
                    </IconButton>
                  </Typography>
                </Grid>
              )}
              <Grid item xs={12} sx={{ padding: 1 }}>
                <DatePicker
                  label="Start Date"
                  value={startDateValue ? dayjs(startDateValue) : null}
                  onChange={(newValue) =>
                    setStartDateValue(
                      newValue ? newValue.format('YYYY-MM-DD') : null
                    )
                  }
                  disabled={isSaving || sendingSave}
                  slotProps={{
                    textField: { fullWidth: true, size: 'small' },
                  }}
                />
              </Grid>
              {editDates && (
                <Grid item xs={12} sx={{ paddingTop: 1, paddingBottom: 1 }}>
                  <DatePicker
                    label="End Date"
                    value={endDateValue ? dayjs(endDateValue) : null}
                    onChange={(newValue) =>
                      setEndDateValue(
                        newValue ? newValue.format('YYYY-MM-DD') : null
                      )
                    }
                    disabled={isSaving || sendingSave}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        size: 'small',
                        helperText: 'Leave blank if currently in position',
                      },
                    }}
                  />
                </Grid>
              )}
              {pageFrom === 'tlc' && editDates && (
                <Grid item xs={12} sx={{ paddingTop: 1, paddingBottom: 1 }}>
                  <FormControl size="small" fullWidth>
                    <InputLabel id="tlc-position-label">Role</InputLabel>
                    <Select
                      labelId="tlc-position-label"
                      value={positionIdValue ?? 'Contact'}
                      label="Role"
                      onChange={(e) =>
                        setPositionIdValue(
                          e.target.value as 'Primary' | 'Contact'
                        )
                      }
                      disabled={isSaving || sendingSave}
                    >
                      <MenuItem value="Primary">Primary</MenuItem>
                      <MenuItem value="Contact">Contact</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              )}
              {pageFrom === 'finance-fund-chairs' &&
                editDates &&
                chairRoleOptions &&
                chairRoleOptions.length > 0 && (
                  <Grid item xs={12} sx={{ paddingTop: 1, paddingBottom: 1 }}>
                    <FormControl size="small" fullWidth>
                      <InputLabel id="fund-chair-role-label">Role</InputLabel>
                      <Select
                        labelId="fund-chair-role-label"
                        value={positionIdValue ?? ''}
                        label="Role"
                        onChange={(e) =>
                          setPositionIdValue(String(e.target.value))
                        }
                        disabled={isSaving || sendingSave}
                      >
                        {chairRoleOptions.map((r) => (
                          <MenuItem key={r.id} value={r.id}>
                            {r.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                )}
              <Grid item xs={12} sx={{ paddingBottom: 1, pt: 1 }}>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={handleSave}
                  disabled={isSaving || sendingSave}
                  sx={{ fontWeight: 'bold' }}
                  startIcon={
                    isSaving || sendingSave ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : undefined
                  }
                >
                  {isSaving || sendingSave ? 'Saving…' : 'Save'}
                </Button>
              </Grid>
            </Grid>
          )}
          {removeRecord && (
            <Box sx={{ py: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Remove {name ?? 'this person'} from {position}? This cannot be
                undone.
              </Typography>
              <ButtonGroup variant="contained" size="small">
                <Button
                  onClick={() => setRemoveRecord(false)}
                  disabled={isSaving || sendingSave}
                >
                  Cancel
                </Button>
                <Button
                  color="error"
                  onClick={handleConfirmRemove}
                  disabled={isSaving || sendingSave}
                  startIcon={
                    isSaving || sendingSave ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : undefined
                  }
                >
                  {isSaving || sendingSave ? 'Deleting…' : 'Delete'}
                </Button>
              </ButtonGroup>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={resetState} disabled={isSaving || sendingSave}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={showSelfRemovalWarning}
        onClose={handleCancelSelfRemovalWarning}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Warning</DialogTitle>
        <DialogContent>
          <Alert severity="warning">
            You are removing yourself from your last leadership role on this
            page. After this change you may lose access to edit leadership here.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelSelfRemovalWarning}>Go back</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleContinueAfterSelfRemovalWarning}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>
      <UserSelectModal
        modalOpen={showUserSelect}
        setModalOpen={setShowUserSelect}
        setUser={handleUserSelect}
        allowSetToMe={false}
        userObjectRet={true}
        chapter={localChapterId}
        spd={spdChapterId}
        activeMembers={true}
        statusFilter="active"
      />
      <MessageCreate
        modalOpen={modalOpen}
        setModalOpen={setModalOpen}
        id={userId}
        name={name}
        toType={'user'}
        screenName={pageFrom}
      />
      <Popover
        open={Boolean(roleHistoryAnchor)}
        anchorEl={roleHistoryAnchor}
        onClose={handleRoleHistoryClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Box sx={{ p: 2, minWidth: 280, maxWidth: 360 }}>
          {roleHistoryLoading && (
            <Typography variant="body2" color="text.secondary">
              Loading...
            </Typography>
          )}
          {roleHistoryError && (
            <Typography variant="body2" color="error">
              {roleHistoryError}
            </Typography>
          )}
          {roleHistoryData && !roleHistoryLoading && (
            <>
              {roleHistoryData.future.length > 0 && (
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 'bold', mb: 1 }}
                >
                  Upcoming
                </Typography>
              )}
              {roleHistoryData.future.map((m, i) => (
                <Typography key={i} variant="body2" sx={{ mb: 0.5 }}>
                  {m.name} (starts {dayjs(m.start_date).format('M/D/YYYY')})
                </Typography>
              ))}
              {roleHistoryData.future.length > 0 &&
                roleHistoryData.past.length > 0 && (
                  <Box sx={{ mt: 1.5, mb: 0.5 }} />
                )}
              {roleHistoryData.past.length > 0 && (
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 'bold', mb: 1 }}
                >
                  Past (last 5 years)
                </Typography>
              )}
              {roleHistoryData.past.map((m, i) => (
                <Typography key={i} variant="body2" sx={{ mb: 0.5 }}>
                  {m.name}{' '}
                  {m.end_date
                    ? `(${dayjs(m.start_date).format('M/D/YYYY')} - ${dayjs(m.end_date).format('M/D/YYYY')})`
                    : `(${dayjs(m.start_date).format('M/D/YYYY')})`}
                </Typography>
              ))}
              {roleHistoryData.future.length === 0 &&
                roleHistoryData.past.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    No additional history for this role.
                  </Typography>
                )}
            </>
          )}
        </Box>
      </Popover>
    </Grid>
  );
}
